import { NavLink, useLocation } from "react-router";
import isotipo from "/vegan/iso_zoom.webp";
import { useState, useEffect, useRef } from "react";
import { FaBarsStaggered } from "react-icons/fa6";
import { FaTimes } from "react-icons/fa";

const navItems = [
  { path: { pathname: "/" }, label: "Inicio" },
  { path: { pathname: "/about" }, label: "Quienes somos" },
  { path: { pathname: "/ourFood" }, hash: "#vegan", label: "Nuestra Comida" },
  { path: { pathname: "/contact", hash: "#contact" }, label: "Contáctanos" },
];

export default function Navbar() {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);

  // Detectar dirección del scroll
  useEffect(() => {
    let lastScrollY = window.scrollY;

    if (!navRef.current) return;
    const thresholdScrollY = navRef.current.offsetHeight;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY < thresholdScrollY) return;

      setIsScrollingDown(currentScrollY > lastScrollY && currentScrollY > 50);
      lastScrollY = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => setIsMenuOpen((v) => !v);

  const getNavbarStyle = () => {
    const styles: { [key: string]: string } = {
      "/ourFood": "bg-black/30 text-white text-lg",
      "/services": "bg-black/35 text-white text-lg",
      "/about": " text-primary text-lg bg-[#C5A537]/35",
      "/": "bg-[#F5F2E4]/35 text-white text-lg",
    };
    return styles[location.pathname] || "bg-black/40 text-black text-lg";
  };

  useEffect(() => {
    if (location.hash === "#contact") {
      const id = location.hash;
      const element = document.querySelector(id);
      if (element) element.scrollIntoView({ behavior: "smooth" });
    }
  }, [location]);

  // Ocultamiento en scroll
  const shouldHideNav = isScrollingDown && !isMenuOpen;

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-200 transition-all duration-300 border-b-2 border-[#C5A537] ${getNavbarStyle()} ${
        shouldHideNav ? "-translate-y-full" : "translate-y-0"
      }`}
      ref={navRef}
    >
      {/* ALTURA FIJA DEL NAVBAR */}
      <div className="max-w-8xl mx-auto px-4 sm:px-6 md:px-12 lg:px-10 flex flex-row items-center gap-4 backdrop-blur-sm h-16 sm:h-20 md:h-24">
        {/* Logo */}
        <a className="flex items-center" href="/">
          <img
            src={isotipo}
            alt="Vegan Vegetarian"
            className="h-10 sm:h-12 md:hidden object-contain"
          />
          <img
            src={isotipo}
            alt="Vegan Vegetarian"
            className="hidden md:block h-14 object-contain"
          />
        </a>

        {/* Botón hamburguesa (solo móvil) */}
        <button
          className="md:hidden ml-auto flex justify-center items-center w-10 h-8 text-primary"
          onClick={toggleMenu}
          aria-label="Menú principal"
        >
          {isMenuOpen ? <FaTimes className="w-7 h-7" /> : <FaBarsStaggered className="w-7 h-7" />}
        </button>

        {/* Menú de navegación (desktop) */}
        <ul className="hidden md:flex flex-row flex-1 h-full justify-center font-medium items-stretch rounded-md">
          {navItems.map(({ path: { pathname, hash }, label }) => (
            <li key={pathname + (hash ?? "")} className="flex h-full">
              <NavLink
                to={{ pathname, hash }}
                className={({ isActive }) =>
                  // cada link ocupa todo el alto y centra el texto verticalmente
                  `flex items-center transition px-3 lg:px-4 ${
                    isActive
                      ? "text-primary text-lg font-montserrat font-semibold bg-gradient-to-b from-transparent to-[#C5A537]"
                      : "text-white hover:text-primary"
                  }`
                }
              >
                <h6>{label}</h6>
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Marca a la derecha */}
        <ul className="hidden md:flex">
          <li>
            <NavLink
              to={"/"}
              className={"flex flex-col items-center justify-center text-[#275823] leading-tight"}
            >
              <h4>Vegan</h4>
              <h4>Vegetarian</h4>
            </NavLink>
          </li>
        </ul>
      </div>

      {/* Menú móvil desplegable */}
      <div
        className={`md:hidden transition-all duration-300 overflow-hidden ${
          isMenuOpen ? "max-h-60 border-b border-gray-200" : "max-h-0"
        } backdrop-blur-sm`}
      >
        <ul className="flex flex-col py-2 px-4 items-center text-center bg-black/70">
          {navItems.map(({ path: { pathname, hash }, label }) => (
            <li key={pathname + (hash ?? "")} className="py-2 w-full">
              <NavLink
                to={{ pathname, hash }}
                className={({ isActive }) =>
                  `block transition py-2 px-4 ${
                    isActive ? "text-primary font-montserrat font-semibold" : "text-white hover:text-primary"
                  }`
                }
                onClick={() => setIsMenuOpen(false)}
              >
                {label}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}
